import { RESULTS_REVIEW_STATUS } from '../constants';
import { CheckboxActive, CheckboxDefault } from '@/assets/svg';
import CustomAsyncAutoComplete from '@/new-components/CustomAsyncAutoComplete';
import CustomDateRange from '@/new-components/CustomDateRange';
import CustomSelectContainer from '@/new-components/CustomSelectOld/CustomSelectContainer';
import {
  Box,
  Toolbar,
  Button,
  Checkbox,
  Paper,
  TextField,
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { isBoolean, isEmpty } from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';

const CustomButton = withStyles({
  label: {
    fontWeight: 600,
  },
})(Button);
const CustomFilterInput = withStyles({
  root: {
    '& .MuiOutlinedInput-root': {
      height: 40,
      borderRadius: 8,
      '& input': {
        padding: 0,
        marginLeft: 0,
      },
      '& input::placeholder': {
        opacity: 1,
        color: '#999999',
      },
    },
  },
})(TextField);

const useStyles = makeStyles(() => ({
  checkboxIcon: {
    padding: 0,
    marginRight: 4,
  },
  tagStyle: {
    maxWidth: 'calc(100% - 60px)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  selectWrapper: {
    '& .MuiIconButton-root:hover': {
      backgroundColor: 'transparent',
    },
    '& .MuiAutocomplete-option': {
      padding: 8,
      borderRadius: 8,
      margin: '2px 8px',
      '&:first-child': {
        marginTop: 8,
      },
      '&:last-child': {
        marginBottom: 8,
      },
      '&.MuiAutocomplete-option[aria-selected="true"]': {
        backgroundColor: '#ffffff',
      },
    },
  },
}));

const GroupToolbar = ({
  filterOption,
  setFilterOption,

  reviewerStatus,
  assignedDoctors,
  setAssignedDoctors,
  isAdminSide,
  futStatus,
  onFilterOptionChange,
  onResetFilterOps,

  screeningDate,
  setScreeningDate,

  signedMonth,
  setSignedMonth,
}) => {
  const classes = useStyles();
  const renderValue = (array, label, defaultLabel) => {
    let value = '';
    if (array.length === 0) value = defaultLabel;
    else if (array.length === 1) value = array[0];
    else value = `${label} (${array.length})`;

    return value;
  };

  const renderDateValue = (array, defaultLabel) => {
    /* 
    1. !startDate && !endDate => defaultLabel
    2. startDate && !endDate => startDate format: DD/MM/YYYY
    3. startDate && endDate
      - if startDate and endDate in the same month, year => format(DD-DD/MM/YYYY)
      - else format(DD/MM/YYYY-DD/MM/YYYY)
    */

    if (isEmpty(array)) return defaultLabel;
    const startDate = moment(array[0]).format('DD/MM/YYYY');
    const endDate = moment(array[1]).format('DD/MM/YYYY');

    if (startDate.trim() === endDate.trim()) {
      return `${startDate} `;
    } else if (moment(array[0]).isSame(moment(array[1]), 'year')) {
      const newStartDate = moment(array[0]).format('DD/MM');
      return `${newStartDate} - ${endDate} `;
    }

    return `${startDate} - ${endDate} `;
  };

  const isEmptyFilterParams = useMemo(() => {
    const filterKeys = Object.keys(filterOption);
    return filterKeys.every((key) =>
      isBoolean(filterOption[key])
        ? !filterOption[key]
        : isEmpty(filterOption[key])
    );
  }, [filterOption]);

  return (
    <Toolbar>
      <Box
        display="flex"
        flex={1}
        justifyContent="space-between"
        alignItems="center"
      >
        <Box display="flex" gridGap={12}>
          <CustomDateRange
            width={200}
            selectedDate={screeningDate || []}
            renderValue={() =>
              renderDateValue(screeningDate || [], 'Select screening date')
            }
            onChange={(startDate, endDate) => {
              if (startDate && endDate) {
                setScreeningDate([startDate, endDate]);
                setFilterOption({
                  ...filterOption,
                  fromDate: startDate.toISOString(),
                  toDate: endDate.toISOString(),
                });
              } else {
                setScreeningDate([]);
                setFilterOption({
                  ...filterOption,
                  fromDate: null,
                  toDate: null,
                });
              }
            }}
          />

          <CustomDateRange
            width={200}
            selectedDate={signedMonth || []}
            renderValue={() =>
              renderDateValue(signedMonth || [], 'Select completed date range')
            }
            onChange={(startDate, endDate) => {
              if (startDate && endDate) {
                setSignedMonth([startDate, endDate]);
                setFilterOption({
                  ...filterOption,
                  lastSignedStartDate: startDate.toISOString(),
                  lastSignedEndDate: endDate.toISOString(),
                });
              } else {
                setSignedMonth([]);
                setFilterOption({
                  ...filterOption,
                  lastSignedStartDate: null,
                  lastSignedEndDate: null,
                });
              }
            }}
          />

          <CustomSelectContainer
            isFullWidth
            filterValue={reviewerStatus || []}
            data={RESULTS_REVIEW_STATUS}
            renderValue={renderValue(
              reviewerStatus.map(
                (it) =>
                  RESULTS_REVIEW_STATUS.find((item) => item.key === it).value
              ),
              'Reviewer statuses',
              'All review statuses'
            )}
            clearable={false}
            onChange={onFilterOptionChange('reviewStatuses')}
            labelSelectAll={'All review statuses'}
            useSelectAll
            checkedAll={RESULTS_REVIEW_STATUS.length === reviewerStatus.length}
          />
          <CustomAsyncAutoComplete
            multiple
            disableCloseOnSelect
            endpoint="fut-backrooms/valid-doctors"
            method="get"
            value={assignedDoctors}
            onChange={(e, value) => {
              setAssignedDoctors(value);
              setFilterOption({
                ...filterOption,
                assignedDoctorIds: (value || []).map((it) => it.id),
              });
            }}
            isFormatResult
            nameKey="fullName"
            getOptionLabel={(option) => option?.fullName || ''}
            renderInput={(params) => (
              <CustomFilterInput
                {...params}
                variant="outlined"
                placeholder={
                  isEmpty(assignedDoctors) ? 'All assigned doctors' : ''
                }
              />
            )}
            renderOption={(option, { selected }) => (
              <Box display="flex" gridGap={4}>
                <Checkbox
                  icon={<CheckboxDefault width={20} height={20} />}
                  checkedIcon={<CheckboxActive width={20} height={20} />}
                  checked={selected}
                  color="primary"
                  className={classes.checkboxIcon}
                />
                <span>{option?.fullName}</span>
              </Box>
            )}
            renderTags={(value) => {
              if (value.length === 1) {
                return (
                  <div className={classes.tagStyle}>{value[0].fullName}</div>
                );
              }
              if (value.length > 1) {
                return <Box>Doctors ({value.length})</Box>;
              }
              return '';
            }}
            PaperComponent={({ children, ...other }) => (
              <Paper
                {...other}
                style={{ width: 320 }}
                className={classes.selectWrapper}
              >
                {children}
              </Paper>
            )}
            style={{ width: assignedDoctors.length === 1 ? 220 : 180 }}
            isSelected={assignedDoctors.length === 1}
          />

          {/* <CustomSelectContainer
            isFullWidth
            filterValue={futStatus || []}
            data={RESULTS_REVIEW_FUT_STATUS}
            renderValue={renderValue(
              futStatus.map(
                (it) =>
                  RESULTS_REVIEW_FUT_STATUS.find((item) => item.key === it)
                    .value
              ),
              'FUT statuses',
              'All FUT statuses'
            )}
            clearable={false}
            onChange={onFilterOptionChange('backroomStatuses')}
            labelSelectAll={'All FUT statuses'}
            useSelectAll
            checkedAll={RESULTS_REVIEW_FUT_STATUS.length === futStatus.length}
          /> */}
        </Box>

        <CustomButton
          color="primary"
          disabled={isEmptyFilterParams}
          onClick={onResetFilterOps}
        >
          Reset
        </CustomButton>
      </Box>
    </Toolbar>
  );
};

export default GroupToolbar;
